import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Clock as ClockIcon, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Clock = ({ 
  value, 
  onChange, 
  placeholder = "Select Time", 
  className = "", 
  inputClassName = "" 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMode, setActiveMode] = useState('hours'); // 'hours' or 'minutes'
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const popoverRef = useRef(null);
  const dialRef = useRef(null);
  const modeRef = useRef(activeMode);
  modeRef.current = activeMode;

  // Convert 24-hour string ("HH:MM") to 12-hour parts
  const to12Hour = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return { hour: 12, minute: 0, period: 'AM' };
    const parts = timeStr.split(':');
    if (parts.length < 2) return { hour: 12, minute: 0, period: 'AM' };
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (isNaN(h) || isNaN(m)) return { hour: 12, minute: 0, period: 'AM' };
    
    const period = h >= 12 ? 'PM' : 'AM';
    let hour = h % 12;
    if (hour === 0) hour = 12;
    return { hour, minute: m, period };
  };

  // Convert 12-hour parts to 24-hour string ("HH:MM")
  const to24Hour = (h, m, p) => {
    let hour24 = h;
    if (p === 'PM' && h < 12) hour24 += 12;
    if (p === 'AM' && h === 12) hour24 = 0;
    return `${String(hour24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const { hour: activeHour, minute: activeMinute, period: activePeriod } = to12Hour(value);

  // Dial geometry
  const dialSize = 216;
  const center = dialSize / 2; // 108px
  const radius = 76; // Distance of numbers from center

  // Angle calculations
  const hourAngle = (activeHour % 12) * 30;
  const minuteAngle = activeMinute * 6;
  const currentAngle = activeMode === 'hours' ? hourAngle : minuteAngle;

  const updateTimeFromPointer = useCallback((clientX, clientY) => {
    if (!dialRef.current) return;
    const rect = dialRef.current.getBoundingClientRect();
    const dx = clientX - (rect.left + center);
    const dy = clientY - (rect.top + center);

    // Angle clockwise from 12 o'clock
    let angleRad = Math.atan2(dx, -dy);
    let angleDeg = angleRad * (180 / Math.PI);
    if (angleDeg < 0) angleDeg += 360;

    if (modeRef.current === 'hours') {
      let h = Math.round(angleDeg / 30);
      if (h === 0) h = 12;
      onChange(to24Hour(h, activeMinute, activePeriod));
    } else {
      let m = Math.round(angleDeg / 6);
      if (m === 60) m = 0;
      onChange(to24Hour(activeHour, m, activePeriod));
    }
  }, [activeHour, activeMinute, activePeriod, onChange, center]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    updateTimeFromPointer(e.clientX, e.clientY);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    updateTimeFromPointer(e.clientX, e.clientY);
  }, [isDragging, updateTimeFromPointer]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      // ONLY advance to minutes when user finishes their click/drag on hours
      if (modeRef.current === 'hours') {
        setTimeout(() => {
          setActiveMode('minutes');
        }, 220);
      }
    }
  }, [isDragging]);

  const handleTouchStart = (e) => {
    if (e.touches.length > 0) {
      setIsDragging(true);
      updateTimeFromPointer(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || e.touches.length === 0) return;
    updateTimeFromPointer(e.touches[0].clientX, e.touches[0].clientY);
  }, [isDragging, updateTimeFromPointer]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('touchend', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  const [popoverStyle, setPopoverStyle] = useState({
    left: 0,
    top: 0
  });

  const updatePosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const popoverWidth = 270;
      const popoverHeight = popoverRef.current?.offsetHeight || 370;
      const padding = 12;

      // Vertical position (smart dropUp calculation)
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldDropUp = spaceBelow < (popoverHeight + 8) && spaceAbove > spaceBelow;

      let top;
      if (shouldDropUp) {
        top = rect.top - popoverHeight - 8;
      } else {
        top = rect.bottom + 8;
      }

      // Clamp vertical position so it stays within viewport
      if (top < padding) {
        top = padding;
      } else if (top + popoverHeight > window.innerHeight - padding) {
        top = Math.max(padding, window.innerHeight - padding - popoverHeight);
      }

      // Horizontal position: align with left edge of input
      let left = rect.left;
      if (left + popoverWidth > window.innerWidth - padding) {
        left = window.innerWidth - padding - popoverWidth;
      }
      if (left < padding) {
        left = padding;
      }

      setPopoverStyle({
        position: 'fixed',
        left: `${left}px`,
        top: `${top}px`,
        zIndex: 999999
      });
    }
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      updatePosition();
    }
    setIsOpen((prev) => !prev);
    setActiveMode('hours');
  };

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    const handleScrollOrResize = (e) => {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      updatePosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updatePosition]);

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (isDragging) return;
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, isDragging]);

  const formatNumber = (num) => String(num).padStart(2, '0');

  // Hours: 12, 1, 2 ... 11
  const hoursList = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  // Minutes: 00, 05, 10 ... 55
  const minutesLandmarks = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  return (
    <div ref={containerRef} className={`relative select-none ${className}`}>
      {/* Input Field Button */}
      <div 
        onClick={handleToggle}
        className={`flex items-center justify-between w-full h-[42px] bg-white border border-slate-200 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary rounded-xl px-3.5 cursor-pointer shadow-sm transition-all duration-200 ${inputClassName}`}
      >
        <span className={`text-xs ${value ? 'text-slate-700' : 'text-slate-400'}`}>
          {value || placeholder}
        </span>
        <ClockIcon size={14} className="text-slate-400 shrink-0" />
      </div>

      {/* Popover Clock Picker (Portaled to document.body to prevent clipping by overflow-hidden containers) */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div 
              ref={popoverRef}
              initial={{ opacity: 0, scale: 0.95, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -4 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              style={popoverStyle}
              className="w-[270px] max-w-[calc(100vw-24px)] bg-white border border-slate-100 rounded-3xl shadow-2xl overflow-hidden flex flex-col items-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Header: Digital Display + Horizontal AM/PM Toggle */}
            <div className="flex items-center justify-between w-full mb-4 px-1 gap-2">
              {/* Digital Time Buttons */}
              <div className="flex items-center bg-slate-50 border border-slate-200/70 rounded-2xl p-1 shadow-inner">
                <button 
                  type="button"
                  onClick={() => setActiveMode('hours')}
                  className={`text-2xl font-bold font-mono transition-all px-2.5 py-1 rounded-xl ${
                    activeMode === 'hours' 
                      ? 'bg-primary text-white shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {formatNumber(activeHour)}
                </button>
                <span className="text-xl font-bold text-slate-300 font-mono px-0.5">:</span>
                <button 
                  type="button"
                  onClick={() => setActiveMode('minutes')}
                  className={`text-2xl font-bold font-mono transition-all px-2.5 py-1 rounded-xl ${
                    activeMode === 'minutes' 
                      ? 'bg-primary text-white shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {formatNumber(activeMinute)}
                </button>
              </div>

              {/* Horizontal AM/PM Segmented Control */}
              <div className="flex items-center bg-slate-100 p-1 rounded-2xl border border-slate-200/70 shadow-inner">
                <button
                  type="button"
                  onClick={() => onChange(to24Hour(activeHour, activeMinute, 'AM'))}
                  className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all ${
                    activePeriod === 'AM' 
                      ? 'bg-primary text-white shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  AM
                </button>
                <button
                  type="button"
                  onClick={() => onChange(to24Hour(activeHour, activeMinute, 'PM'))}
                  className={`px-3 py-1.5 text-xs font-bold rounded-xl transition-all ${
                    activePeriod === 'PM' 
                      ? 'bg-primary text-white shadow-sm' 
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  PM
                </button>
              </div>
            </div>

            {/* Circular Clock Dial */}
            <div 
              ref={dialRef}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              className="w-[216px] h-[216px] rounded-full bg-slate-50 border border-slate-200/70 relative cursor-pointer shadow-inner flex items-center justify-center select-none"
            >
              {/* Central Pivot Dot */}
              <div className="w-3 h-3 bg-primary rounded-full z-30 shadow-sm pointer-events-none" />

              {/* Smooth Animated Clock Hand */}
              <div 
                className="absolute origin-bottom z-10 pointer-events-none"
                style={{
                  width: '2px',
                  height: `${radius}px`,
                  backgroundColor: 'var(--color-primary, #0f8b8d)',
                  bottom: `${center}px`,
                  left: `${center - 1}px`,
                  transform: `rotate(${currentAngle}deg)`,
                  transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                {/* Pointer Tip Highlight Circle */}
                <div 
                  className="w-8 h-8 rounded-full bg-primary absolute -top-4 -left-[15px] shadow-md flex items-center justify-center"
                />
              </div>

              {/* Numbers Layer */}
              <AnimatePresence mode="wait">
                <motion.div 
                  key={activeMode}
                  initial={{ opacity: 0, scale: 0.88 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.08 }}
                  transition={{ duration: 0.18, ease: "easeOut" }}
                  className="absolute inset-0 w-full h-full pointer-events-none"
                >
                  {activeMode === 'hours' ? (
                    hoursList.map((h) => {
                      const isSelected = activeHour === h;
                      const angleRad = (h * 30 * Math.PI) / 180;
                      const x = center + radius * Math.sin(angleRad);
                      const y = center - radius * Math.cos(angleRad);

                      return (
                        <div
                          key={h}
                          style={{
                            left: `${x - 16}px`,
                            top: `${y - 16}px`,
                          }}
                          className={`absolute w-8 h-8 flex items-center justify-center text-xs font-bold font-mono transition-colors duration-150 z-20 ${
                            isSelected ? 'text-white font-black' : 'text-slate-700'
                          }`}
                        >
                          {h}
                        </div>
                      );
                    })
                  ) : (
                    minutesLandmarks.map((m) => {
                      const isSelected = activeMinute === m;
                      const angleRad = (m * 6 * Math.PI) / 180;
                      const x = center + radius * Math.sin(angleRad);
                      const y = center - radius * Math.cos(angleRad);

                      return (
                        <div
                          key={m}
                          style={{
                            left: `${x - 16}px`,
                            top: `${y - 16}px`,
                          }}
                          className={`absolute w-8 h-8 flex items-center justify-center text-xs font-bold font-mono transition-colors duration-150 z-20 ${
                            isSelected ? 'text-white font-black' : 'text-slate-700'
                          }`}
                        >
                          {formatNumber(m)}
                        </div>
                      );
                    })
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Footer Done & Mode Indicators */}
            <div className="w-full flex items-center justify-between border-t border-slate-100 pt-3 mt-3 px-1">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                Selecting: <span className="text-primary font-bold capitalize">{activeMode}</span>
              </span>
              <button
                type="button"
                disabled={!value}
                onClick={() => {
                  if (value) setIsOpen(false);
                }}
                className={`flex items-center gap-1 px-4 py-1.5 rounded-xl text-xs font-bold tracking-wide transition-all shadow-sm ${
                  !value
                    ? 'bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed shadow-none opacity-60'
                    : 'bg-primary text-white hover:opacity-95 active:scale-95 cursor-pointer'
                }`}
                title={!value ? 'Please select a time on the clock' : undefined}
              >
                <Check size={14} /> Done
              </button>
            </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default Clock;
